import { HttpClient, HttpErrorResponse, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map, share, tap, finalize } from 'rxjs/operators';
// Models
import { AutopilotOperation, AutopilotTrajectory, MultipleApiResponse, SearchArea } from '@mocca-technologies/model';

import {
    APOPERATIONS,
    APTRAJECTORIES,
    ALL_APOPERATIONS,
    ALL_APTRAJECTORIES,
    OBTAIN_APTRAJECTORY_FROM_FILE
} from 'src/app/utils/routing/routes.model';
// Services
import { CoreService } from './core.service';

@Injectable()
export class AutopilotService {
    private readonly DEBUG = false;
    private readonly EARTH_RADIUS = 6378100;

    // TODO: Hide behaviorsubject, make only its own observable public
    private isLoading$ = new BehaviorSubject<boolean>(false);

    apOperations$ = new BehaviorSubject<AutopilotOperation[]>([]);
    apTrajectories$ = new BehaviorSubject<AutopilotTrajectory[]>([]);

    private apTrajectoryCache = new Map<string, AutopilotTrajectory>();

    private trajectorySelected = new Subject<AutopilotTrajectory>();
    trajectorySelected$ = this.trajectorySelected.asObservable().pipe(share());

    private operationSelected = new Subject<AutopilotOperation>();
    operationSelected$ = this.operationSelected.asObservable().pipe(share());

    hideOperationsList = false;

    public get loading$(): Observable<boolean> {
        return this.isLoading$.asObservable();
    }

    constructor(private http: HttpClient,
      private coreService: CoreService,
    ) {}

    getLocationResults(location: string) {
        const route = 'https://nominatim.openstreetmap.org';
        const httpParams = new HttpParams().set('q', location).set('format', 'json');

        return this.http.get(route, { params: httpParams });
    }

    getAllAutopilotTrajectories(){
        return this.http.get<MultipleApiResponse>(ALL_APTRAJECTORIES, {
            withCredentials: true,
            observe: 'response'
        }).pipe(
            map(
                (res: HttpResponse<MultipleApiResponse>) => {
                    if (res?.status === 204) {
                        return [];
                    } else {
                        this.DEBUG && console.debug('All autopilot trajectories response', res);
                        const trajectories = res.body.responseObject as AutopilotTrajectory[] || [];
                        return trajectories.map((el) => ({ ...el, expanded: false }));
                    }
                }
            ),
            tap(
                (res) => {
                    this.DEBUG && console.debug('Autopilot Trajectories', res);
                    this.apTrajectories$.next(res);
                }
            ),
            catchError((err) => {
                this.DEBUG && console.error('Error fetching autopilot trajectories', err);
                return of([]);
            })
        ).subscribe();
    }

    getAllAutopilotOperations(searchArea: SearchArea = null): Observable<AutopilotOperation[]> {
        this.isLoading$.next(true);

        let httpParams = null;
        if (searchArea) {
            httpParams = new HttpParams()
                .set('latitude', searchArea.latlng.lat.toString())
                .set('longitude', searchArea.latlng.lng.toString())
                .set('radius', (searchArea.radius / this.EARTH_RADIUS).toString());
        }

        return this.http
            .get<MultipleApiResponse>(ALL_APOPERATIONS, {
                params: httpParams,
                observe: 'response',
            })
            .pipe(
                map((res: HttpResponse<MultipleApiResponse>) => {
                    if (res?.status === 204) {
                        return [];
                    }
                    return (res?.body?.responseObject as AutopilotOperation[]).map(el => ({
                        ...el,
                        expanded: false
                    }));
                }),
                tap((res) => {
                    this.apOperations$.next(res);
                }),
                finalize(() => {
                    this.isLoading$.next(false);
                }),
                catchError((error: HttpErrorResponse) => {
                    this.coreService.showServerResponse(error?.status, error?.error);
                    return throwError(() => error);
                }),
            );
    }

    getDetailedOperation(operationUUID: string): Observable<HttpResponse<MultipleApiResponse>> {
        const httpParams = new HttpParams().set('operationId', operationUUID);

        return this.http
            .get<MultipleApiResponse>(APOPERATIONS, {
                params: httpParams,
                observe: 'response',
            })
            .pipe(tap((res: HttpResponse<MultipleApiResponse>) => {
                const operation = res?.body?.responseObject as AutopilotOperation;
                this.getDetailedTrajectory(operation.mainTrajectory.trajectoryUUID);
                this.DEBUG && console.debug('Detailed Operation', res)
            }),
            catchError((error: HttpErrorResponse) => {
                throw error;
            })
            );
    }

    getDetailedTrajectory(trajectoryUUID: string): Observable<HttpResponse<MultipleApiResponse>> {

        const httpParams = new HttpParams().set('trajectoryId', trajectoryUUID);

        return this.http
            .get<MultipleApiResponse>(APTRAJECTORIES, {
                params: httpParams,
                observe: 'response',
            })
            .pipe(
                tap((res: HttpResponse<MultipleApiResponse>) => {
                    this.trajectorySelected.next(res?.body?.responseObject as AutopilotTrajectory);
                }),
                catchError((error: HttpErrorResponse) => {
                    throw error;
                })
            );
    }

    createAutopilotTrajectory(trajectory: AutopilotTrajectory): Observable<HttpResponse<any>> {
        let apTrajectoryExt = {
            name: trajectory.name,
            version: trajectory.version,
            description: trajectory.description,
            path: trajectory.path,
            searchKeys: trajectory.searchKeys,
            type: trajectory.type.valueOf(),
            detail: trajectory.detail.valueOf(),
            status: trajectory.status.valueOf(),
            allCmd: trajectory.allCommandList,
            allSel: trajectory.selectionList,
        }

        let apTrajectory = {
            apTrajectory: apTrajectoryExt
        }

        return this.http.post<MultipleApiResponse>(
            APTRAJECTORIES,
            { type: "mocca-json", trajectory: JSON.stringify(apTrajectory) },
            { observe: 'response',
              withCredentials: true
            }
        );
    }

    updateAutopilotTrajectory(organizationID: string, trajectory: AutopilotTrajectory): Observable<HttpResponse<any>> {
        let apTrajectoryExt = {
            uuid: trajectory.uniqueId,
            name: trajectory.name,
            version: trajectory.version,
            description: trajectory.description,
            path: trajectory.path,
            searchKeys: trajectory.searchKeys,
            type: trajectory.type,
            detail: trajectory.detail,
            status: trajectory.status,
            allCmd: trajectory.allCommandList,
            allSel: trajectory.selectionList,
        }

        let apTrajectory = {
            apTrajectory: apTrajectoryExt
        }

        return this.http.put<MultipleApiResponse>(
            APTRAJECTORIES,
            { organizationId: organizationID, type: "mocca-json", trajectory: JSON.stringify(apTrajectory) },
            { observe: 'response',
              withCredentials: true
            }
        );
    }

    setSelectedOperation(operation: AutopilotOperation) {
        this.operationSelected.next(operation);
    }

    obtainAutopilotTrajectoryFromFile(file: File, type: string): Observable<HttpResponse<MultipleApiResponse>> {
        const formData: FormData = new FormData();
        formData.append('file', file);

        const httpParams = new HttpParams().set('type', type);

        // Make sure to set the content type to 'multipart/form-data'
        const options : Object = {
            withCredentials: true, // Optional, if you need to send credentials like cookies
            params: httpParams,
            observe: 'response'
        };

        return this.http.post<HttpResponse<MultipleApiResponse>>(
            OBTAIN_APTRAJECTORY_FROM_FILE,
            formData,
            options
        );
    }

    getDetailedTrajectoryCached(trajectoryUUID: string): Observable<AutopilotTrajectory> {
        const cached = this.apTrajectoryCache.get(trajectoryUUID);
        if (cached) {
            return new Observable((observer) => {
                observer.next(cached);
                observer.complete();
            });
        }

        const httpParams = new HttpParams().set('trajectoryId', trajectoryUUID);

        return this.http
            .get<MultipleApiResponse>(APTRAJECTORIES, {
                params: httpParams,
                observe: 'response',
            })
            .pipe(
                map((res: HttpResponse<MultipleApiResponse>) => {
                    return res?.body?.responseObject as AutopilotTrajectory;
                }),
                tap((trajectory: AutopilotTrajectory) => {
                    if (trajectory) {
                        this.apTrajectoryCache.set(trajectoryUUID, trajectory);
                        this.trajectorySelected.next(trajectory);
                    }
                }),
                catchError((error: HttpErrorResponse) => {
                    throw error;
                })
            )
    }
}
